import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import { Logo } from "../components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { uploadPortalInfo, uploadPortalFile } from "../lib/api";

const DOC_TYPES = ["Aadhar Card", "PAN Card", "Bank Statement", "Salary Slip", "GST Certificate", "ITR", "Other"];

export default function UploadPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid, obj=ok
  const [docType, setDocType] = useState("Aadhar Card");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState([]);

  useEffect(() => {
    uploadPortalInfo(token).then(setInfo).catch(() => setInfo(null));
  }, [token]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const r = await uploadPortalFile(token, fd);
      setDone((d) => [r.filename, ...d]);
      toast.success("Document uploaded!");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-900 flex items-center justify-center p-4" data-testid="upload-portal">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        {info === undefined && <Loader2 className="w-7 h-7 text-gold animate-spin mx-auto" />}
        {info === null && (
          <div className="card-dark rounded-2xl p-8 text-center">
            <h1 className="font-heading text-2xl font-bold text-white">Link not valid</h1>
            <p className="text-slate-400 text-sm mt-2">This upload link is invalid or has expired. Please request a new one from your dashboard.</p>
          </div>
        )}
        {info && (
          <div className="card-dark rounded-2xl p-8 gold-glow">
            <div className="flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-widest"><ShieldCheck className="w-4 h-4" /> Secure Upload</div>
            <h1 className="font-heading text-2xl font-extrabold text-white mt-2">Hi {info.name},</h1>
            <p className="text-slate-400 text-sm mt-1">Upload your KYC documents below. They attach directly and securely to your Genesis profile.</p>

            <div className="mt-5">
              <label className="text-slate-300 text-sm mb-1.5 block">Document Type</label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="portal-doc-type" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                  {DOC_TYPES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <label className="mt-4 flex items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl p-6 cursor-pointer hover:border-gold/40 transition-colors" data-testid="portal-upload-label">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin text-gold" /> : <Upload className="w-5 h-5 text-gold" />}
              <span className="text-slate-300 text-sm">{uploading ? "Uploading..." : "Tap to choose a file (PDF / image)"}</span>
              <input type="file" className="hidden" onChange={onFile} accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid="portal-file-input" />
            </label>

            {done.length > 0 && (
              <div className="mt-5 space-y-2" data-testid="portal-uploaded-list">
                {done.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300 bg-obsidian-900/60 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4 text-approval shrink-0" />
                    <FileText className="w-4 h-4 text-gold shrink-0" />
                    <span className="truncate">{f}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="text-center text-slate-600 text-xs mt-5">Genesis Group of Companies · Secure document intake</p>
      </div>
    </div>
  );
}
